import { Box, Button, CircularProgress, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { AlertBanner, SearchInput } from "../../components/FormComponents";
import { useLoginAction } from "../../hooks/useLoginAction";
import { getValidationErrors, useLoginStore } from "../../store/useLoginStore";
import { useSystemStateStore } from "../../store/useSystemStateStore";
import { EdgeSystemStatus, EdgeSystemType, EdgeUserType } from "../../types";
import { getInitialLoginData } from "../../utils";
import { LoginFormField } from "./LoginFormField";
import LoginOTPInput from "./LoginOTPInput";
import { LoginPageWrapper } from "./LoginPageWrapper";
import { RequestOTP } from "./RequestOTP/RequestOTP";
import { LOGIN_PAGE_NS } from "./i18n";

export const LoginForm = () => {
  const { t } = useTranslation(LOGIN_PAGE_NS);
  const { palette } = useTheme();
  const {
    systemState: {
      [EdgeSystemType.CloudConnection]: { status: cloudConnectionStatus }
    }
  } = useSystemStateStore();
  const { doLogin } = useLoginAction();
  const { search: urlSearchQuery } = useLocation();

  const {
    loginHasFailed,
    isSubmitButtonLoading,
    loginData,
    setLoginHasFailed,
    updateLoginData,
    setValidationErrors
  } = useLoginStore();

  const [showRequestTokenForm, setShowRequestTokenForm] = useState(false);
  const [isOfflineLoginClicked, setIsOfflineLoginClicked] = useState(false);

  useEffect(() => {
    const initialLoginData = getInitialLoginData(urlSearchQuery);
    updateLoginData(initialLoginData);
  }, [updateLoginData, urlSearchQuery]);
  
  const currentValidationErrors = useMemo(() => getValidationErrors(loginData), [loginData]);
  
  const userType = loginData.userType;
  const isBargeLogin = userType === EdgeUserType.Barge;

  const cloudWasNotConnected = useRef<boolean>(false);  
  const cloudIsDisconnected = cloudConnectionStatus === EdgeSystemStatus.Error;
  const cloudIsReconnectingAfterError = cloudWasNotConnected.current && cloudConnectionStatus === EdgeSystemStatus.Ongoing;
  const cloudIsNotConnected = cloudIsDisconnected || cloudIsReconnectingAfterError;

  useEffect(() => {
    cloudWasNotConnected.current = cloudIsNotConnected;
  }, [cloudIsNotConnected]);
  
  const showOtplessLoginLink = !isBargeLogin && cloudIsNotConnected;
  const isOtpResetLinkEnabled = !currentValidationErrors.userInfo;
  const isOtplessLoginLinkEnabled = !currentValidationErrors.userInfo;

  const selectUserInput = (
    <SearchInput
      value={loginData.userType}
      onChange={event => updateLoginData({ userType: event.target.value as EdgeUserType })}
      options={[EdgeUserType.Customer, EdgeUserType.Surveyor]}
      label={t("form.userType.label")}
    />
  );

  const handleOtplessLoginLinkClick = () => {
    const validationErrors = getValidationErrors(loginData);
    setValidationErrors({ ...validationErrors, otp: false, any: false });

    if (isOtplessLoginLinkEnabled) {
      setLoginHasFailed(true);
      setShowRequestTokenForm(true);
      setIsOfflineLoginClicked(true);
    }
  };

  const otplessLoginLink = (
    <Box sx={{
      textAlign: "center",
      backgroundColor: palette.mode === "dark" ? "#293542" : "#D9EBFF",
      p: 2, paddingX: 3, mt: 2
    }}>
      If you can't connect to the internet, you can still log in without a
      One Time Password, but it may affect future identification
      <Box>
        <Box
            component="span"
            sx={{
              textDecoration: "underline",
              color: "#3AA4E6",
              cursor: "pointer",
              p: 1
            }}
            onClick={handleOtplessLoginLinkClick}
        >
          Login without One Time Password
        </Box>
      </Box>
    </Box>
  );

  const handleResetOtpClick = () => {
    const validationErrors = getValidationErrors(loginData);
    setValidationErrors({ ...validationErrors, otp: false, any: false });
    
    if (isOtpResetLinkEnabled) {
      setLoginHasFailed(true);
      setShowRequestTokenForm(true);
      setIsOfflineLoginClicked(false);
    }
  };

  const otpResetText = (
    <>
      Please enter the One Time Password that has been sent to your email, or click&nbsp;
      <Box
        component="span"
        sx={{
          textDecoration: "underline",
          color: "#3AA4E6",
          cursor: "pointer"
        }}
        onClick={handleResetOtpClick}
      >
        {t("button.requestOtp")}
      </Box>
      &nbsp;if you haven't received it yet
    </>
  );

  const otpInput = (
    <Box sx={{ mb: 2.5 }}>
      <LoginOTPInput
        value={loginData.otp ?? ''}
        onValueChange={val => {
          setLoginHasFailed(false);
          updateLoginData({ otp: val });
        }}
        hasError={loginHasFailed}
        footerMessage={
          showOtplessLoginLink
            ? null
            : <Trans i18nKey="EnterOrRequestOtp">{otpResetText}</Trans>
        }
        lowerFooter={showOtplessLoginLink ? otplessLoginLink : null}
      />
    </Box>
  );

  const userForms = {
    [EdgeUserType.Barge]:
      <>
        <LoginFormField propertyName="email" />
        <LoginFormField propertyName="password" />
      </>,
    [EdgeUserType.Customer]:
      <>
        {selectUserInput}
        <LoginFormField propertyName="displayName" />
        <LoginFormField propertyName="vesselName" />
        <LoginFormField propertyName="rank" />
        <LoginFormField propertyName="email" />
        {otpInput}
      </>,
    [EdgeUserType.Surveyor]: 
      <>
        {selectUserInput}
        <LoginFormField propertyName="displayName" />
        <LoginFormField propertyName="licenseNumber" />
        <LoginFormField propertyName="vesselName" />
        <LoginFormField propertyName="email" />
        {otpInput}
      </>,
    [EdgeUserType.None]: null,
  };

  const failedLoginWarning = loginHasFailed ? (
    <AlertBanner
      sx={{ width: "460px" }}
      title="Sign-in failed"
      errorMessage={
        <Trans i18nKey="genericError">
          {isBargeLogin ? "Invalid email or password" : otpResetText}
        </Trans>
      }
    />
  ) : null;

  const loginButton = (
    <Button
      disabled={isSubmitButtonLoading || currentValidationErrors.any}
      sx={{ backgroundColor: "#1D82C0" }}
      onClick={() => doLogin()}
      variant="contained"
    >
    {
      isSubmitButtonLoading
        ? <CircularProgress size={18} />
        : t(`button.${isBargeLogin ? 'user' : 'guest'}`)
    }
    </Button>
  );

  const loginForm = (
    <LoginPageWrapper>
      {failedLoginWarning}
      <Box sx={{ width: "460px" }}>
        {userForms[userType]}
      </Box>
      {loginButton}
    </LoginPageWrapper>
  );

  const requestOTPForm = (
    <RequestOTP
      loginAction={doLogin}
      goBack={() => setShowRequestTokenForm(false)}
      isOfflineRequest={isOfflineLoginClicked}
    />
  );

  const form = showRequestTokenForm ? requestOTPForm : loginForm;

  return form;
}

